import { defineStore, type StateTree } from 'pinia';
import type {
  LoadingArea,
  PatchLoadingArea,
  PostLoadingArea,
  PutLoadingArea,
  RecordEntityId,
} from '@centric-os/types';

/**
 * Interface representing the state of the LoadingArea store.
 * @typedef {Object} State
 * @property {LoadingArea[]} loadingAreas - The loading areas for the current site.
 * @property {LoadingArea | null} loadingArea - The loading area currently being edited.
 */
interface State extends StateTree {
  loadingAreas: LoadingArea[];
  loadingArea: LoadingArea | null;
}

/**
 * Creates a store for managing AVRIDE robot delivery loading areas.
 * @param {string} [storeId='loadingArea'] - The unique ID of the store.
 * @returns {import('pinia').Store} The Pinia store instance.
 */
export function useLoadingAreaStore(storeId = 'loadingArea') {
  const store = defineStore(storeId, {
    state: (): State => ({
      loadingAreas: [],
      loadingArea: null,
    }),
    actions: {
      /**
       * Fetches all loading areas for a site.
       * @param {RecordEntityId} siteId - The ID of the site to fetch loading areas for.
       * @returns {Promise<void>} A promise that resolves when the loading areas are fetched.
       */
      async getLoadingAreas(siteId: RecordEntityId): Promise<void> {
        const response = await this.serviceApi.get_location_group_loading_areas(String(siteId));

        this.loadingAreas = response?.loading_areas ?? [];
      },

      /**
       * Creates a new loading area and appends it to the store.
       * @param {RecordEntityId} siteId - The ID of the site to create the loading area in.
       * @param {PostLoadingArea} payload - The loading area to create.
       * @returns {Promise<void>} A promise that resolves when the loading area is created.
       */
      async createLoadingArea(siteId: RecordEntityId, payload: PostLoadingArea): Promise<void> {
        const response = await this.serviceApi.post_location_group_loading_area(
          String(siteId),
          payload,
        );

        this.loadingAreas = [...this.loadingAreas, response];
      },

      /**
       * Updates an existing loading area and replaces it in the store.
       * @param {RecordEntityId} siteId - The ID of the site the loading area belongs to.
       * @param {string} loadingAreaId - The ID of the loading area to update.
       * @param {PutLoadingArea} payload - The updated loading area.
       * @returns {Promise<void>} A promise that resolves when the loading area is updated.
       */
      async updateLoadingArea(
        siteId: RecordEntityId,
        loadingAreaId: string,
        payload: PutLoadingArea,
      ): Promise<void> {
        const response = await this.serviceApi.put_location_group_loading_area(
          String(siteId),
          loadingAreaId,
          payload,
        );

        this.loadingAreas = this.loadingAreas.map((area) =>
          area.id === loadingAreaId ? response : area,
        );
      },

      /**
       * Replaces a loading area's per-destination travel times.
       * @param {RecordEntityId} siteId - The ID of the site the loading area belongs to.
       * @param {string} loadingAreaId - The ID of the loading area to update.
       * @param {PatchLoadingArea['delivery_time']} deliveryTime - Destination name to minutes.
       * @returns {Promise<void>} A promise that resolves when the times are saved.
       */
      async setLoadingAreaDeliveryTimes(
        siteId: RecordEntityId,
        loadingAreaId: string,
        deliveryTime: PatchLoadingArea['delivery_time'],
      ): Promise<void> {
        const response = await this.serviceApi.patch_location_group_loading_area(
          String(siteId),
          loadingAreaId,
          { delivery_time: deliveryTime },
        );

        this.loadingAreas = this.loadingAreas.map((area) =>
          area.id === loadingAreaId ? response : area,
        );
      },

      /**
       * Deletes (archives) a loading area and removes it from the store.
       * @param {RecordEntityId} siteId - The ID of the site the loading area belongs to.
       * @param {string} loadingAreaId - The ID of the loading area to delete.
       * @returns {Promise<void>} A promise that resolves when the loading area is deleted.
       */
      async deleteLoadingArea(siteId: RecordEntityId, loadingAreaId: string): Promise<void> {
        await this.serviceApi.delete_location_group_loading_area(String(siteId), loadingAreaId);

        this.loadingAreas = this.loadingAreas.filter((area) => area.id !== loadingAreaId);
      },
    },
    pagination: false,
  });
  return store();
}
